import React, { useState, useCallback } from 'react'
import {
  Box,
  Divider,
  Collapse,
} from '@mui/material'
import {
  Download as DownloadIcon,
  BarChart as StatsIcon,
} from '@mui/icons-material'
import { Grid } from '@mui/material'
import { useGetHistoryQuery } from '@store/api/historyApi'
import { useGetSessionsQuery } from '@store/api/sessionApi'
import { useAppSelector } from '@store/hooks'
import ActiveSessionCard from '@components/sessions/ActiveSessionCard'
import HistoryFilters, { HistoryFilterState } from './HistoryFilters'
import HistoryList from './HistoryList'
import HistorySummary from './HistorySummary'
import PageHeader from '@components/layout/PageHeader'
import { trackFeature } from '@utils/trackFeature'
import { useNavigate } from 'react-router-dom'

const DEFAULT_FILTERS: HistoryFilterState = {
  search: '',
  brandId: '',
  dateFrom: '',
  dateTo: '',
  rating: '',
}

const History: React.FC = () => {
  const navigate = useNavigate()
  const { token } = useAppSelector((state) => state.auth)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<HistoryFilterState>(DEFAULT_FILTERS)
  const [showStats, setShowStats] = useState(true)
  const queryParams = {
    page,
    ...(filters.search && { search: filters.search }),
    ...(filters.brandId && { brandId: filters.brandId }),
    ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
    ...(filters.dateTo && { dateTo: filters.dateTo }),
    ...(filters.rating && { rating: filters.rating }),
  }

  const { data, isLoading, error } = useGetHistoryQuery(queryParams)

  const { data: sessionsData } = useGetSessionsQuery({ includeDrafts: true, limit: 10 })
  const activeSessions = sessionsData?.sessions?.filter(s => s.isDraft && s.startTime && !s.endTime) || []

  const handleFiltersChange = useCallback((newFilters: HistoryFilterState) => {
    setFilters(newFilters)
    setPage(1)
  }, [])

  const handleExport = (format: 'csv' | 'json') => {
    trackFeature('overflow_action', { action: `export_${format}` })
    const params = new URLSearchParams()
    params.set('format', format)
    if (filters.search) params.set('search', filters.search)
    if (filters.brandId) params.set('brandId', filters.brandId)
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
    if (filters.dateTo) params.set('dateTo', filters.dateTo)
    if (filters.rating) params.set('rating', filters.rating)

    const url = `/api/v1/history/export?${params.toString()}`
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('Authorization', `Bearer ${token ?? ''}`)
    fetch(url, { headers: { Authorization: `Bearer ${token ?? ''}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob)
        link.href = objectUrl
        link.download = `smoking-history.${format}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(objectUrl)
      })
  }

  const handleToggleStats = () => {
    trackFeature('overflow_action', { action: showStats ? 'hide_stats' : 'show_stats' })
    setShowStats((v) => !v)
  }

  return (
    <>
      <PageHeader
        title="History"
        overflowItems={[
          {
            id: 'stats',
            label: showStats ? 'Hide statistics' : 'Show statistics',
            icon: <StatsIcon fontSize="small" />,
            onClick: handleToggleStats,
          },
          {
            id: 'export-csv',
            label: 'Export CSV',
            icon: <DownloadIcon fontSize="small" />,
            onClick: () => handleExport('csv'),
          },
          {
            id: 'export-json',
            label: 'Export JSON',
            icon: <DownloadIcon fontSize="small" />,
            onClick: () => handleExport('json'),
          },
        ]}
      />

      <Collapse in={showStats}>
        <HistorySummary />
        <Divider sx={{ mb: 3 }} />
      </Collapse>

      <HistoryFilters filters={filters} onChange={handleFiltersChange} />

      {activeSessions.length > 0 && (
        <Box mb={3}>
          <Grid container spacing={3}>
            {activeSessions.map((session) => (
              <Grid item xs={12} md={6} key={session.id}>
                <ActiveSessionCard session={session} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <HistoryList
        sessions={data?.sessions ?? []}
        pagination={data?.pagination ?? { page: 1, limit: 20, totalCount: 0, totalPages: 0 }}
        isLoading={isLoading}
        error={error}
        page={page}
        onPageChange={setPage}
        onEmptyStateCta={() => {
          trackFeature('empty_state_cta', { action: 'start_session' })
          navigate('/app/journal')
        }}
      />
    </>
  )
}

export default History
